import React, { useState } from 'react';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useDesign } from '../../contexts/DesignContext';

const FloatingThemeToggle = () => {
  const { resolvedTheme, setTheme } = useTheme();
  const { isStudio } = useDesign();
  const isDark = resolvedTheme === 'dark';
  const [hovered, setHovered] = useState(false);

  const handleClick = () => {
    setTheme(isDark ? 'light' : 'dark');
  };

  // D32 (doc 120, owner 2026-09-18): the owner asked for this control back, which
  // **overrules docs/app-map/101 §10** ("no floating dev-tool style theme toggle;
  // theme lives in Appearance"). 101 §10's objection was that it *read* as a dev
  // tool — an appearance objection, not a feature one — so Studio renders it in
  // the kit's own language instead of un-hiding classic's styling: the 36px
  // `.st-ibtn` at the kit's 8px control radius, `.studio-float` for the one pop
  // shadow and hairline (§4), `.studio-focus` for the §5 focus ring, and the kit
  // motion tokens. Classic's branch below is untouched, byte for byte.
  // Behaviour is classic's: same handler, same `data-testid`, same 24px offset,
  // same z-index, and still desktop-only (classic's `hidden lg:flex`, reproduced
  // as a 1024 breakpoint in §33.15 so the kit's grid centring survives).
  if (isStudio) {
    return (
      <button
        data-testid="floating-theme-toggle"
        onClick={handleClick}
        aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
        className="st-ibtn studio-float studio-focus st-themetoggle"
      >
        {isDark ? <Moon /> : <Sun />}
      </button>
    );
  }

  return (
    <button
      data-testid="floating-theme-toggle"
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      className={`
        hidden lg:flex
        fixed bottom-6 right-6 z-40
        w-9 h-9 rounded-xl
        items-center justify-center
        shadow-lg transition-all duration-300 ease-in-out
        cursor-pointer overflow-hidden
        ${isDark
          ? hovered
            ? 'bg-white shadow-white/20'
            : 'bg-[#2d2f33] border border-white/10 shadow-black/30'
          : hovered
            ? 'bg-[#1e1f22] shadow-black/30'
            : 'bg-white border border-slate-200 shadow-slate-200/60'
        }
      `}
    >
      <div className="relative w-4 h-4">
        {/* Current icon (slides out on hover) */}
        <div
          className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ease-in-out ${
            hovered ? 'translate-x-6 opacity-0' : 'translate-x-0 opacity-100'
          }`}
        >
          {isDark
            ? <Moon className="w-4 h-4 text-slate-300" />
            : <Sun className="w-4 h-4 text-slate-700" />
          }
        </div>
        {/* Hover icon (slides in from left) */}
        <div
          className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ease-in-out ${
            hovered ? 'translate-x-0 opacity-100' : '-translate-x-6 opacity-0'
          }`}
        >
          {isDark
            ? <Sun className="w-4 h-4 text-slate-700" />
            : <Moon className="w-4 h-4 text-slate-300" />
          }
        </div>
      </div>
    </button>
  );
};

export default FloatingThemeToggle;
